import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type Ref,
} from 'react';
import { buildTimeAxis, packShifts, timePixel, type TimeScale, type TimeShift } from '@/lib/timeGrid';
import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import { Tooltip } from './Tooltip';
import { cn } from './cn';
import { Disclosure } from './Disclosure';
export interface TimeRowGroup {
  id: string;
  title: string;
  detail?: string;
  rowIds: readonly string[];
}

export interface TimeRow {
  id: string;
  title: string;
  detail?: string;
}
const tones = {
  info: 'bg-info-soft text-info-strong border-info-line',
  success: 'bg-success-soft text-success-strong border-success-line',
  warning: 'bg-warning-soft text-warning-strong border-warning-line',
};

/** A controlled calendar axis. Data, loading, permissions and actions belong to its owner. */
export function TimeGrid({
  rows,
  shifts,
  scale,
  anchor,
  periods,
  timeZone = 'Europe/Moscow',
  now,
  loading = false,
  error,
  hasMore = false,
  onLoadMore,
  onShiftClick,
  viewportRef,
  label = 'Сетка времени',
  rowHeading = 'Участник',
  groups,
  expandedGroups,
  onExpandedGroupsChange,
  highlightWeekends = false,
  popup,
}: {
  rows: readonly TimeRow[];
  groups?: readonly TimeRowGroup[];
  expandedGroups?: readonly string[];
  onExpandedGroupsChange?: (ids: string[]) => void;
  highlightWeekends?: boolean;
  popup?: { id: string; activeShiftId?: string };
  shifts: readonly TimeShift[];
  scale: TimeScale;
  anchor: string;
  periods: number;
  timeZone?: string;
  now?: number;
  loading?: boolean;
  error?: string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onShiftClick?: (shift: TimeShift, trigger: HTMLElement) => void;
  viewportRef?: Ref<HTMLDivElement>;
  label?: string;
  rowHeading?: string;
}) {
  const viewport = useRef<HTMLDivElement>(null);
  useImperativeHandle(viewportRef, () => viewport.current!);
  const [visible, setVisible] = useState({ left: 0, width: 0, viewport: 0 });
  const [closedGroups, setClosedGroups] = useState<string[]>([]);
  const measureView = useCallback(() => {
    const el = viewport.current;
    if (!el) return;
    const label = el.querySelector<HTMLElement>('[data-row-label], [data-time-header] > div');
    const next = {
      left: el.scrollLeft,
      viewport: el.clientWidth,
      width: Math.max(0, el.clientWidth - (label?.getBoundingClientRect().width ?? 0)),
    };
    setVisible((previous) =>
      previous.left === next.left && previous.width === next.width && previous.viewport === next.viewport
        ? previous
        : next,
    );
  }, []);
  useLayoutEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const observer = new ResizeObserver(measureView);
    observer.observe(el);
    measureView();
    return () => observer.disconnect();
  }, [measureView, rows.length]);
  const help = useId();
  const requested = useRef(false),
    lastLeft = useRef(0);
  useEffect(() => {
    requested.current = false;
  }, [periods, loading, error, scale, anchor]);
  const load = () => {
    if (loading || requested.current || periods >= 24 || !onLoadMore || (!hasMore && !error)) return;
    requested.current = true;
    onLoadMore();
  };
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    if (now !== undefined) return;
    const timer = window.setInterval(() => setClock(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, [now]);
  const axis = buildTimeAxis(scale, anchor, periods, timeZone);
  const pending = Boolean((loading || error) && periods < 24);
  const display = pending ? buildTimeAxis(scale, anchor, periods + 1, timeZone) : axis;
  const units = display.boundaries.slice(0, -1);
  const loadedUnits = axis.boundaries.length - 1;
  const width = units.length * axis.stepWidth,
    loadedWidth = loadedUnits * axis.stepWidth;
  const current = now ?? clock;
  const today = Number.isFinite(current) ? timePixel(current, axis) : -1;
  const visibleToday = today >= 0 && today < loadedWidth;
  const dateFormat = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const monthFormat = new Intl.DateTimeFormat('ru-RU', { timeZone, month: 'long', year: 'numeric' });
  const unitFormat = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    ...(scale === 'day' || scale === 'fortnight'
      ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
      : { day: 'numeric' }),
  });
  const weekdayFormat = new Intl.DateTimeFormat('ru-RU', { timeZone, weekday: 'short' });
  const shortDateFormat = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });
  const weekendFormat = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' });
  const weekend = (at: number) => highlightWeekends && ['Sat', 'Sun'].includes(weekendFormat.format(at));
  const rangeFormat = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'shortOffset',
  });
  const axisGroups: { label: string; start: number; count: number; pending: boolean }[] = [];
  units.forEach((at, i) => {
    const groupLabel = (
      scale === 'day' ? dateFormat : scale === 'fortnight' ? shortDateFormat : monthFormat
    ).format(at);
    const isPending = i >= loadedUnits;
    const last = axisGroups.at(-1);
    if (last && last.label === groupLabel && last.pending === isPending) last.count++;
    else axisGroups.push({ label: groupLabel, start: i, count: 1, pending: isPending });
  });

  const usedRows = new Set<string>(),
    usedGroups = new Set<string>();
  const groupedRows = (groups ?? [])
    .filter((g) => {
      if (usedGroups.has(g.id)) return false;
      usedGroups.add(g.id);
      return true;
    })
    .map((group) => ({
      ...group,
      rows: group.rowIds.flatMap((id) => {
        const row = rows.find((r) => r.id === id);
        if (!row || usedRows.has(id)) return [];
        usedRows.add(id);
        return [row];
      }),
    }));
  const ungroupedRows = rows.filter((row) => !usedRows.has(row.id));
  const openedGroups =
    expandedGroups ??
    groupedRows.filter((group) => !closedGroups.includes(group.id)).map((group) => group.id);
  const renderRow = (row: TimeRow) => {
    const packed = packShifts(
      shifts.filter(
        (s) =>
          s.rowId === row.id &&
          Date.parse(s.end) > axis.boundaries[0] &&
          Date.parse(s.start) < axis.boundaries.at(-1)!,
      ),
    );
    const lanes = Math.max(1, ...packed.map((p) => p.lane + 1));
    const height = 24 + lanes * 40;
    return (
      <div
        key={row.id}
        data-time-row={row.id}
        className="flex border-b border-line last:border-b-0"
        style={{ height }}
      >
        <div
          data-row-label=""
          className="sticky left-0 z-raised flex w-24 shrink-0 flex-col justify-center border-r border-line bg-card px-3 @md/time:w-48"
          title={row.title + (row.detail ? ' · ' + row.detail : '')}
        >
          <p className="truncate text-sm font-medium">{row.title}</p>
          {row.detail && <p className="mt-1 truncate text-xs text-fg-muted">{row.detail}</p>}
        </div>
        <div className="relative z-base shrink-0 overflow-hidden" style={{ width }}>
          {units.map((at, i) => (
            <span
              key={at}
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-y-0 border-r border-line',
                weekend(at) && 'bg-muted',
              )}
              style={{ left: i * axis.stepWidth, width: axis.stepWidth }}
            />
          ))}
          {visibleToday && (
            <span
              data-today-line
              aria-hidden
              className="pointer-events-none absolute inset-y-0 z-base border-l-2 border-brand"
              style={{ left: today }}
            />
          )}
          {packed.map(({ shift, lane }) => {
            const left = Math.max(0, timePixel(Date.parse(shift.start), axis));
            const right = Math.min(loadedWidth, timePixel(Date.parse(shift.end), axis));
            const title =
              shift.title +
              ', ' +
              row.title +
              ', ' +
              rangeFormat.format(Date.parse(shift.start)) +
              ' — ' +
              rangeFormat.format(Date.parse(shift.end)) +
              (shift.tentative ? ' · Предварительно' : '');
            const style = { left, width: Math.max(1, right - left), top: 12 + lane * 40 };
            const barTone = tones[shift.tone ?? 'info'];
            const innerLeft = left + 1,
              innerRight = Math.max(innerLeft, right - 1);
            const labelLeft = Math.max(0, Math.min(innerRight - innerLeft, visible.left - innerLeft));
            const labelWidth = visible.width
              ? Math.max(
                  0,
                  Math.min(innerRight, visible.left + visible.width) - Math.max(innerLeft, visible.left),
                )
              : innerRight - innerLeft;
            const content = (
              <span
                data-time-label
                className={labelWidth < 32 ? 'sr-only' : 'block shrink-0 truncate px-2'}
                style={labelWidth < 32 ? undefined : { marginLeft: labelLeft, width: labelWidth }}
              >
                {shift.title}
              </span>
            );
            const className = cn(
              'absolute z-raised flex h-8 min-w-0 items-center overflow-hidden rounded-sm border text-left text-xs font-medium',
              barTone,
              shift.tentative && 'border-dashed',
            );
            return (
              <Tooltip
                key={shift.id}
                suppressed={popup?.activeShiftId === shift.id}
                content={<span className="block max-w-72 whitespace-normal">{title}</span>}
              >
                {onShiftClick ? (
                  <button
                    key={shift.id}
                    type="button"
                    aria-haspopup={popup ? 'dialog' : undefined}
                    aria-expanded={popup ? popup.activeShiftId === shift.id : undefined}
                    aria-controls={popup?.activeShiftId === shift.id ? popup.id : undefined}
                    data-time-state={shift.tentative ? 'tentative' : undefined}
                    data-time-shift={shift.id}
                    aria-label={title}
                    data-tone={shift.tone ?? 'info'}
                    className={cn(
                      className,
                      'transition-colors duration-fast hover:brightness-105 focus-visible:z-dock focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                    )}
                    style={style}
                    onClick={(event) => onShiftClick(shift, event.currentTarget)}
                  >
                    {content}
                  </button>
                ) : (
                  <div
                    key={shift.id}
                    data-time-shift={shift.id}
                    aria-label={title}
                    data-tone={shift.tone ?? 'info'}
                    className={className}
                    tabIndex={0}
                    style={style}
                  >
                    {content}
                  </div>
                )}
              </Tooltip>
            );
          })}
          {pending && (
            <div
              data-time-pending
              className="absolute inset-y-0 flex items-start px-3 pt-3"
              style={{ left: loadedWidth, width: width - loadedWidth }}
            >
              <Skeleton still={Boolean(error)} className="h-8 w-full" />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      data-slot="time-grid"
      className="@container/time isolate min-w-0 overflow-hidden rounded-lg border border-line bg-card"
    >
      <div
        ref={viewport}
        data-grid-viewport=""
        data-scrollport=""
        role="region"
        aria-label={label}
        aria-describedby={help}
        tabIndex={0}
        aria-busy={loading}
        className="h-96 scroll-pt-16 scroll-pl-24 overflow-auto focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none @md/time:scroll-pl-48"
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget || event.altKey) return;
          const el = event.currentTarget;
          let left = el.scrollLeft,
            top = el.scrollTop;
          const control = event.ctrlKey || event.metaKey;
          if (control && !['Home', 'End'].includes(event.key)) return;
          if (event.key === 'ArrowRight') left += axis.stepWidth;
          else if (event.key === 'ArrowLeft') left -= axis.stepWidth;
          else if (event.key === 'ArrowDown') top += 40;
          else if (event.key === 'ArrowUp') top -= 40;
          else if (event.key === 'PageDown') top += Math.max(40, el.clientHeight - 64);
          else if (event.key === 'PageUp') top -= Math.max(40, el.clientHeight - 64);
          else if (event.key === 'Home') {
            left = 0;
            if (control) top = 0;
          } else if (event.key === 'End') {
            left = el.scrollWidth;
            if (control) top = el.scrollHeight;
          } else return;
          event.preventDefault();
          el.scrollTo({ left, top, behavior: 'instant' });
        }}
        onScroll={(event) => {
          measureView();
          const el = event.currentTarget,
            forward = el.scrollLeft > lastLeft.current;
          lastLeft.current = el.scrollLeft;
          if (
            forward &&
            el.clientWidth &&
            el.clientHeight &&
            hasMore &&
            !loading &&
            !error &&
            el.scrollWidth - el.clientWidth - el.scrollLeft < 160
          )
            load();
        }}
      >
        <div className="w-max min-w-full">
          <div data-time-header className="sticky top-0 z-dock flex h-16 border-b border-line bg-card">
            <div className="sticky left-0 z-raised flex w-24 shrink-0 items-center border-r border-line bg-card px-3 text-xs font-semibold text-fg-muted @md/time:w-48">
              {rowHeading}
            </div>
            <div className="relative shrink-0" style={{ width }}>
              <div className="flex h-6 border-b border-line bg-muted">
                {axisGroups.map((group) => (
                  <div
                    key={group.start}
                    title={scale === 'fortnight' ? dateFormat.format(units[group.start]) : undefined}
                    data-time-group={group.label}
                    className="shrink-0 border-r border-line text-xs leading-6 font-medium text-fg"
                    style={{ width: group.count * axis.stepWidth }}
                  >
                    {group.pending ? (
                      <Skeleton still={Boolean(error)} className="mt-2 ml-3 w-20" />
                    ) : (
                      <span className="sticky left-24 inline-block max-w-full truncate px-3 align-top @md/time:left-48">
                        {group.label}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex h-10">
                {units.map((at, i) => (
                  <div
                    key={at}
                    data-time-column={at}
                    aria-label={rangeFormat.format(at)}
                    title={rangeFormat.format(at)}
                    className={cn(
                      'flex shrink-0 flex-col items-center justify-center border-r border-line text-xs text-fg-muted',
                      weekend(at) && 'bg-muted',
                    )}
                    style={{ width: axis.stepWidth }}
                  >
                    {i >= loadedUnits ? (
                      <Skeleton still={Boolean(error)} className="w-6" />
                    ) : (
                      <>
                        <span className="font-medium tabular-nums">{unitFormat.format(at)}</span>
                        {scale !== 'day' && scale !== 'fortnight' && (
                          <span className="text-2xs">{weekdayFormat.format(at)}</span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              {visibleToday && (
                <span
                  data-today-label
                  className="pointer-events-none absolute bottom-0 z-raised rounded-sm bg-brand-soft px-1 text-2xs font-semibold whitespace-nowrap text-brand-fg"
                  style={{ left: Math.min(today, loadedWidth - 64) }}
                >
                  Сегодня
                </span>
              )}
            </div>
          </div>
          <div data-grid-body="">
            {groups?.length ? (
              <>
                {groupedRows.map((group) => (
                  <Disclosure
                    key={group.id}
                    variant="group"
                    size="sm"
                    stickyHeader
                    headerWidth={visible.viewport || undefined}
                    contentSpacing="none"
                    title={group.title}
                    description={group.detail}
                    region={false}
                    open={openedGroups.includes(group.id)}
                    onOpenChange={(open) => {
                      const next = open
                        ? [...openedGroups, group.id]
                        : openedGroups.filter((id) => id !== group.id);
                      if (expandedGroups === undefined)
                        setClosedGroups(groupedRows.map((g) => g.id).filter((id) => !next.includes(id)));
                      onExpandedGroupsChange?.(next);
                    }}
                  >
                    {group.rows.map(renderRow)}
                  </Disclosure>
                ))}
                {ungroupedRows.map(renderRow)}
              </>
            ) : (
              rows.map(renderRow)
            )}
          </div>
        </div>
        {!rows.length && (
          <EmptyState
            title="Строк пока нет"
            description="Добавь участников или ресурсы, чтобы показать события на шкале."
            className="sticky left-0 rounded-none border-0"
          />
        )}
      </div>
      <div className="flex flex-col gap-3 border-t border-line px-4 py-3 @md/time:flex-row @md/time:items-center @md/time:justify-between">
        <div className="min-w-0 flex-1">
          <p
            role={error ? 'alert' : 'status'}
            aria-live={error ? 'assertive' : 'polite'}
            className={error ? 'text-xs text-danger-strong' : 'text-xs text-fg-muted'}
          >
            {error ??
              (loading
                ? 'Добавляем следующий период'
                : 'Загружено по ' + dateFormat.format(axis.boundaries.at(-1)! - 1))}
          </p>
          <p id={help} className="mt-1 text-xs text-fg-muted">
            Tab — перейти к сетке и событиям. Стрелки — прокрутить, Home / End — к краям, Page Up / Down — по
            строкам.
          </p>
        </div>
        {onLoadMore && (
          <Button
            variant="secondary"
            size="sm"
            aria-disabled={loading || (!hasMore && !error) || periods >= 24}
            className="self-start aria-disabled:cursor-default aria-disabled:opacity-50 @md/time:self-auto"
            onClick={load}
          >
            {error
              ? 'Повторить'
              : loading
                ? 'Загружаем период…'
                : hasMore
                  ? 'Следующий период'
                  : 'Все периоды'}
          </Button>
        )}
      </div>
    </div>
  );
}
